import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import {
  generateKeyPair, exportPublicKey, encryptPrivateKeyWithPin,
  decryptPrivateKeyWithPin, importPublicKey,
  encryptMessage, decryptMessage
} from '@/utils/crypto';

const E2EContext = createContext(null);

export const useE2E = () => useContext(E2EContext);

export function E2EProvider({ children, token, loginPin }) {
  const [ready, setReady] = useState(false);
  const privateKeyRef = useRef(null);
  const publicKeyCacheRef = useRef({});
  const initRef = useRef(false);

  // Auto-initialize E2E silently using stored PIN
  const initWithPin = useCallback(async (pin) => {
    if (!token || !pin || initRef.current) return;
    initRef.current = true;
    try {
      const statusRes = await axios.get(`${API}/e2e/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (statusRes.data.e2e_enabled && statusRes.data.has_keys) {
        // Already set up — decrypt private key with PIN
        const keysRes = await axios.get(`${API}/e2e/my-keys`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const privateKey = await decryptPrivateKeyWithPin({
          encrypted_private_key: keysRes.data.encrypted_private_key,
          salt: keysRes.data.salt,
          iv: keysRes.data.iv
        }, pin);
        privateKeyRef.current = privateKey;
        setReady(true);
      } else {
        // First time — generate keys, encrypt with PIN, store
        const keyPair = await generateKeyPair();
        const publicKeyStr = await exportPublicKey(keyPair.publicKey);
        const encrypted = await encryptPrivateKeyWithPin(keyPair.privateKey, pin);

        await axios.post(`${API}/e2e/setup`, {
          public_key: publicKeyStr,
          encrypted_private_key: encrypted.encrypted_private_key,
          salt: encrypted.salt,
          iv: encrypted.iv
        }, { headers: { Authorization: `Bearer ${token}` } });

        privateKeyRef.current = keyPair.privateKey;
        setReady(true);
      }
    } catch (err) {
      console.warn('E2E init:', err.message);
      initRef.current = false;
    }
  }, [token]);

  // Reset on logout
  useEffect(() => {
    if (!token) {
      privateKeyRef.current = null;
      publicKeyCacheRef.current = {};
      setReady(false);
      initRef.current = false;
    }
  }, [token]);

  // Auto-init when PIN is available from login
  useEffect(() => {
    if (token && loginPin && !initRef.current) {
      initWithPin(loginPin);
    }
  }, [token, loginPin, initWithPin]);

  // Get another user's public key (cached)
  const getPeerPublicKey = useCallback(async (userId) => {
    if (publicKeyCacheRef.current[userId]) return publicKeyCacheRef.current[userId];
    try {
      const res = await axios.get(`${API}/e2e/keys/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.data.e2e_enabled || !res.data.public_key) return null;
      const key = await importPublicKey(res.data.public_key);
      publicKeyCacheRef.current[userId] = key;
      return key;
    } catch {
      return null;
    }
  }, [token]);

  // Encrypt a message for a specific peer
  const encrypt = useCallback(async (plaintext, peerUserId) => {
    if (!privateKeyRef.current) return null;
    const peerKey = await getPeerPublicKey(peerUserId);
    if (!peerKey) return null;
    return encryptMessage(plaintext, privateKeyRef.current, peerKey);
  }, [getPeerPublicKey]);

  // Decrypt a message from a specific peer
  const decrypt = useCallback(async (ciphertext, peerUserId) => {
    if (!privateKeyRef.current) return null;
    const peerKey = await getPeerPublicKey(peerUserId);
    if (!peerKey) return null;
    return decryptMessage(ciphertext, privateKeyRef.current, peerKey);
  }, [getPeerPublicKey]);

  return (
    <E2EContext.Provider value={{
      ready, initWithPin, encrypt, decrypt, getPeerPublicKey
    }}>
      {children}
    </E2EContext.Provider>
  );
}
